import { Button, ChakraSelect, FormControl, FormLabel, Stack, useDisclosure } from '@elkaso-app/web-design';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  isActive?: {
    value: string;
    label: string;
  };
};

const statusOptions = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export const StatusFilterButton = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { setPageParams, getPageParams } = usePageParams();
  const { isActive } = getPageParams();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting },
  } = useForm<TInputs>();

  const onSubmit = useCallback(
    (data: TInputs) => {
      const currentParams = getPageParams();
      const newParams = { ...currentParams };

      // Handle isActive parameter properly
      if (data.isActive?.value !== undefined) {
        const boolValue = data.isActive.value === 'true';
        newParams.isActive = boolValue;
      } else {
        // If no option is selected, remove the parameter
        delete newParams.isActive;
      }

      setPageParams(newParams);
      onClose();
    },
    [setPageParams, getPageParams, onClose]
  );

  const onClear = useCallback(() => {
    const currentParams = getPageParams();
    const newParams = { ...currentParams };
    delete newParams.isActive;
    setPageParams(newParams);
    reset();
    onClose();
  }, [getPageParams, setPageParams, reset, onClose]);

  useEffect(() => {
    if (isOpen) {
      const selectedStatus =
        isActive !== undefined && isActive !== null
          ? statusOptions.find((option) => option.value === String(isActive))
          : undefined;

      reset({
        isActive: selectedStatus,
      });
    }
  }, [isOpen, isActive, reset]);

  const hasFilters = isActive !== undefined && isActive !== null;

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Prevent form submission from closing the modal
  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      e.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit]
  );

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={hasFilters}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleFormSubmit} onClick={(e) => e.stopPropagation()}>
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Controller
              name='isActive'
              control={control}
              render={({ field }) => (
                <ChakraSelect
                  {...field}
                  placeholder='Select status...'
                  options={statusOptions}
                  isClearable
                  onChange={(selectedOption) => {
                    field.onChange(selectedOption);
                  }}
                />
              )}
            />
          </FormControl>

          <Stack direction='row' justify='flex-end' spacing={2}>
            <Button variant='outline' colorScheme='red' onClick={onClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='gray' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='blue' type='submit' isLoading={isSubmitting}>
              Filter
            </Button>
          </Stack>
        </Stack>
      }
    />
  );
};
